import { createError } from '../../../../../lib/error';
import {
  normalizeIdentifier,
  castToBoolean,
  debounce,
} from '../../../../../lib/util';

// Old (working with old stuff)
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

// New try
// import dayGridPlugin from '../../../../../vendor/fullcalendar/packages/daygrid';
// import interactionPlugin from '../../../../../vendor/fullcalendar/packages/interaction';

// import tooltip from "angular-ui-bootstrap/src/tooltip";

class FullCalendarController {

  constructor(
    moment,
    $templateCache,
    $scope,
    $element,
    $window,
    $q,
    $timeout,
    DEBUG,
    showAvailabilityModal,
    showErrorModal,
    StaffUserService,
    DateTimeService,
    DataSyncService,
    PositionService,
    ProgramService,
    RecapDefinitionService,
    TaskQueueService,
    LogService
  ) {
    "ngInject";
    this.showAvailabilityModal = showAvailabilityModal;
    this.showErrorModal = showErrorModal;
    this.StaffUserService = StaffUserService;
    this.DateTimeService = DateTimeService;
    this.DataSyncService = DataSyncService;
    this.PositionService = PositionService;
    this.ProgramService = ProgramService;
    this.RecapDefinitionService = RecapDefinitionService;
    this.TaskQueueService = TaskQueueService;
    this.LogService = LogService;

    this.moment = moment;
    this.$scope = $scope;
    this.$element = $element;
    this.$window = $window;
    this.$q = $q;
    this.$timeout = $timeout;
    this.DEBUG = castToBoolean(DEBUG);

    this._currentDate = moment().toDate();
    this.calendarView = 'month';
    this._events = [];
    this._eventSources = [];

    this._calendar = null;

    this._clickedDate = null;

    this._calendarAspectRatio = 1.35;
    this._calendarMinHeightPx = 380;

    this._fullCalendarOptions = {
      columnHeaderFormat: 'dddd',
      dayMaxEventRows: true,
      editable: true,
      plugins: [
        dayGridPlugin,
        interactionPlugin,
      ],
      aspectRatio: this._calendarAspectRatio,
      themeSystem: 'bootstrap',
      headerToolbar: {
        // left: 'prev,next today',
        // center: 'title',
        // right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        left: 'title',
        center: '',
        right: 'prev,today,next',
      },

      eventDrop: info => this.onCalendarEventDrop(info),

      dragScroll: false, // if true, autoscroller breaks if we simulate a click to close
      eventDragStart: info => this.onCalendarEventDragStart(info),
      eventDragStop: info => this.onCalendarEventDragStop(info),
      moreLinkDidMount: info => {
        this.LogService.debug(`moreLinkDidMount:`, info);
      },
      moreLinkWillUnmount: info => {
        this.LogService.debug(`moreLinkWillUnmount:`, info);
      },
      eventClick: info => this.onCalendarEventClick(info),
      dateClick: info => this.onDateClick(info),
      windowResize: debounce(info => this.onWindowResize(info), 1000, true, `FullCalendarController windowResize'`),
    };
  };

  $onInit() {
    this.$scope.$on('StaffUserService.refreshPositionsComplete', () => {
      this._positionsChanged();
      this.$timeout(() => this._ensureCalendarSize({ message: 'refreshPositionsComplete'}));
    });
    this.$scope.$on('DashboardScreen.calendarCollapseChanged', () => {
      this.LogService.debug(`FullCalendarController collapse changed; need to send resize event`);
      this._positionsChanged();
      this.$timeout(() => this._ensureCalendarSize({ message: 'calendarCollapseChanged' }));
    });
    this._positionsChanged();
    this.$timeout(() => this._ensureCalendarSize({ message: '$onInit' }));

    this._orientationChangeListener = debounce(() => {
      this.LogService.debug(`FullCalendarController._orientationChangeListener()... calling this.onWindowOrientationChange`);
      this.onWindowOrientationChange();
    }, 1000, true, 'FullCalendarController orientationchange');
    this.$window.addEventListener('orientationchange', this._orientationChangeListener);
  }

  $onDestroy() {
    this.$window.removeEventListener('orientationchange', this._orientationChangeListener);
  }

  get events() {
    return this._events;
  }

  get eventSources() {
    return this._eventSources;
  }

  get fullCalendarOptions() {
    return this._fullCalendarOptions;
  }

  get dataSyncWindowIndicatorEvents() {
    const events = [];
    const syncWindowDefinition = this.DataSyncService && this.DataSyncService.syncWindowDefinition;
    const daysForward = syncWindowDefinition && syncWindowDefinition.params.shifts_days_forward;
    const daysBack = syncWindowDefinition && syncWindowDefinition.params.shifts_days_back;
    if (daysForward) {
      // Add background event to block off future
      const daysForwardStart = new Date;
      const daysForwardEnd = new Date;
      daysForwardStart.setDate(daysForwardStart.getDate() + daysForward + 1);
      daysForwardEnd.setDate(daysForwardEnd.getDate() + 10 * 365);
      events.push({
        // title: `Current sync window includes ${daysForward} days forward`,
        title: `No data`,
        start: daysForwardStart,
        end: daysForwardEnd,
        allDay: true,
      });
    }

    if (daysBack) {
      // Add background event to block off future
      const daysBackStart = new Date;
      daysBackStart.setHours(0, 0, 0, 0);
      daysBackStart.setDate(daysBackStart.getDate() - (10 * 365));
      const daysBackEnd = new Date;
      daysBackEnd.setHours(0, 0, 0, 0);
      daysBackEnd.setDate(daysBackEnd.getDate() - daysBack);
      events.push({
        // title: `Current sync window includes ${daysBack} days back`,
        title: `No data`,
        start: daysBackStart,
        end: daysBackEnd,
        allDay: true,
      });
    }

    return events;
  }

  generateEventSources() {
    const standardEventSource = {
      events: this.events,
    };
    const dataSyncWindowIndicatorEventSource = {
      events: this.dataSyncWindowIndicatorEvents,
      display: 'background',
      editable: false,
      backgroundColor: '#ccc',
    };
    const availabilityEventSource = {
      events: this.generateAvailabilityEvents(),
      allDay: true,
      // display: 'background',
      editable: false,
    };
    const eventSources = [
      standardEventSource,
      dataSyncWindowIndicatorEventSource,
      availabilityEventSource,
    ];
    if (this.DEBUG) {
      eventSources.push(this.generateFakeEventSource());
    }
    return eventSources;
  }

  generateFakeEventSource() {
    return {
      events: this.generateFakeEvents(),
    };
  }

  generateFakeEvents(start = null, numDays = 2, eventsPerDay = 40) {
    if (!start) {
      start = new Date();
      start.setHours(8);
      start.setMinutes(0);
      start.setSeconds(0);
    }
    const startDate = new Date(start);
    const currDate = new Date(startDate);
    const events = [];
    const minPerEvent = (eventsPerDay <= 10) ? 60 : (600 / eventsPerDay);
    const msPerEvent = minPerEvent * 60 * 1000;
    for (let day = 0; day < numDays; day++) {
      currDate.setTime(startDate.getTime());
      currDate.setDate(startDate.getDate() + day);
      for (let n = 0; n < eventsPerDay; n++) {
        const eventNum = (day * eventsPerDay) + n + 1;
        const eventStart = new Date();
        eventStart.setTime(currDate.getTime() + (msPerEvent * n));
        const eventEnd = new Date();
        eventEnd.setTime(eventStart.getTime() + msPerEvent);
        events.push({
          title: `Event ${eventNum}`,
          start: eventStart,
          end: eventEnd,
          allDay: false,
          editable: true,
          extendedProps: {
            positionId: 0,
            address: '123 Fake St, Fake City, IL',
          },
        });
      }
    }
    return events;
  }

  generateAvailabilityEvents() {
    const syncWindowDefinition = this.DataSyncService && this.DataSyncService.syncWindowDefinition;
    const daysBack = syncWindowDefinition && syncWindowDefinition.params.shifts_days_back;
    // MJOL-2361 the availability calendar should always show the data going forward quite a long way. The api call to get the available dates has no range limit anyway.
    const maxDaysForward = 180;
    const availDaysForward = maxDaysForward;
    const maxDaysBack = 90;
    const availDaysBack = Math.min(daysBack, maxDaysBack);
    const formatDateString = dateObj => this.moment(dateObj).format('YYYY-MM-DD');
    const availabilityByDate = {};
    const events = [];

    /*
     * First block off all unavailable dates
     */
    this.staffUser.unavailableDates?.forEach(d => {
      const dateString = formatDateString(d);
      availabilityByDate[dateString] = false;
    });

    /*
     * Next loop over all dates in availability window
     */
    const now = new Date();
    const showAvailableDates = false;
    for (let dayOffset = availDaysBack * -1; dayOffset <= availDaysForward; dayOffset++) {
      const date = new Date(now.getTime() + (dayOffset * 24 * 60 * 60 * 1000));
      const dateString = formatDateString(date);
      const available = (availabilityByDate[dateString] !== false);
      if (!available || showAvailableDates) {
        events.push(this._createAvailabilityEventForDate(date, available));
      }
    }

    return events;
  }

  _createAvailabilityEventForDate(date, available = true) {
    const start = this.DateTimeService.parseDate(date);
    const cls = available ? 'available' : 'unavailable';
    const title = cls.replace(/^\w/, c => c.toUpperCase());
    const event = {
      title,
      allDay: true,
      display: 'background',
      start: start,
      end: start,
      editable: false,
      classNames: [cls],
    };
    return event;
  }

  convertPositionsToEvents(positions) {
    return positions?.map(p => this._createEventFromPosition(p)) || [];
  }

  _createEventFromPosition(position) {
    const eventTimezoneName = position.event && position.event.timezoneName;
    // this.LogService.debug(`CalendarController.convertPositionsToEvents eventTimezoneName: '${eventTimezoneName}' for position position:`, position);

    const startTimeLocal = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(position.start_time, eventTimezoneName);
    const endTimeLocal = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(position.end_time, eventTimezoneName);

    const startTimeShifted = this.DateTimeService.shiftDateObjectFromLocal(startTimeLocal, eventTimezoneName);
    const endTimeShifted = this.DateTimeService.shiftDateObjectFromLocal(endTimeLocal, eventTimezoneName);

    const addressStr = `${position.street_address1}, ${position.city}, ${position.state}`;
    return {
      title: position.venue_name,
      start: startTimeShifted,
      end: endTimeShifted,
      allDay: false,
      editable: position.canEdit && !position.isLockedPostRecap,
      extendedProps: {
        positionId: position.id,
        address: addressStr,
      },
    };
  }

  _getProgramById(programId) {
    const id = normalizeIdentifier(programId);
    return this.$q.when(this.staffUser)
      .then(staffUser => staffUser && staffUser.programs || this.$q.reject(createError({
        error: 'MissingProgramsForStaffUser',
        message: 'Unable to retrieve programs for staff user',
      })))
      .then(programs => programs.find(p => normalizeIdentifier(p.id) === id))
      .then(program => program || this.$q.reject(createError({
        error: 'UnableToFindProgram',
        message: `Unable to retrieve program id=${id}`,
      })))
      .then(program => {
        this.LogService.debug(`FullCalendarController._getProgramById(${programId}) returning:`, program);
        return program;
      });
  }

  _getProgramAndRecapDefinitionForPosition(position) {
    return this._getProgramById(position.programs_id)
      .then(program => program || this.$q.reject(createError({
        error: 'UnableToRetrieveProgramForPosition',
        message: `Unable to retrieve program id=${position.programs_id} for position id=${position.id}`,
      })))
      .then(program => {
        const event = position.event;
        const recapDefinitionId = event && normalizeIdentifier(event.events_recaps_id);
        if (!recapDefinitionId) {
          return this.$q.reject(createError({
            error: 'UnableToRetrieveRecapDefinitionForPosition',
            message: `Unable to retrieve recap definition information for position id=${position.id}`,
          }));
        }
        return this.$q.when(this.RecapDefinitionService.getById(recapDefinitionId))
          .then(recapDefinition => {
            this.LogService.debug(`FullCalendarController._getProgramAndRecapDefinitionForPosition(id=${position.id}) got recap definition:`, recapDefinition);
            if (!recapDefinition) {
              return this.$q.reject(createError({
                error: 'UnableToFindRecapDefinitionForPosition',
                message: `Unable to retrieve recap definition id=${recapDefinitionId} for position id=${position.id}`,
              }));
            }
            this.LogService.debug(`FullCalendarController._getProgramAndRecapDefinitionForPosition(id=${position.id}) got program:`, program, `recapDefinition:`, recapDefinition);
            return {
              program,
              recapDefinition,
            };
          });
      });
  }

  _validatePositionTimes(position, newStartTime, newEndTime) {
    return this._getProgramAndRecapDefinitionForPosition(position)
      .then(info => {
        const {program, recapDefinition} = info;
        const formatDateForDisplay = m => m.format('YYYY-MM-DD');
        const programStartDate = this.moment(program.start_date);
        const programEndDate = this.moment(program.end_date);
        const recapAvailableStartDate = this.moment(recapDefinition.recap_available_events_start_time);
        const recapAvailableEndDate = this.moment(recapDefinition.recap_available_events_end_time);

        // Ensure position times are wrapped in moment
        newStartTime = this.moment(newStartTime);
        newEndTime = this.moment(newEndTime);

        this.LogService.log(
          `FullCalendarController._validatePositionTimes(position.id=${position.id} newStartTime=${newStartTime} newEndTime=${newEndTime}`,
          {
            programStartDate,
            programEndDate,
            recapAvailableStartDate,
            recapAvailableEndDate,
            newStartTime,
            newEndTime,
            program,
            recapDefinition,
          }
        );

        if (newStartTime.isBefore(programStartDate)) {
          return this.$q.reject(createError({
            error: 'RequestedDateIsOutsideRange',
            message: `The requested date is out of range. The program start date is ${formatDateForDisplay(programStartDate)}`,
          }));
        } else if (newStartTime.isBefore(recapAvailableStartDate)) {
          return this.$q.reject(createError({
            error: 'RequestedDateIsOutsideRange',
            message: `The requested date is out of range. The recap start date is ${formatDateForDisplay(recapAvailableStartDate)}`,
          }));
        } else if (newEndTime.isAfter(this.moment(programEndDate).add(1, 'day'))) {
          return this.$q.reject(createError({
            error: 'RequestedDateIsOutsideRange',
            message: `The requested date is out of range. The program end date is ${formatDateForDisplay(programEndDate)}`,
          }));
        } else if (newEndTime.isAfter(this.moment(recapAvailableEndDate).add(1, 'day'))) {
          return this.$q.reject(createError({
            error: 'RequestedDateIsOutsideRange',
            message: `The requested date is out of range. The recap end date is ${formatDateForDisplay(recapAvailableEndDate)}`,
          }));
        } else {
          return true;
        }
      })
    ;
  }

  _updatePositionTimes(position, newStartTime, newEndTime) {
    return this.$q.when(this._validatePositionTimes(position, newStartTime, newEndTime))
      .then(() => {
        // Create task
        const task = this.TaskQueueService.createTask('PositionUpdateTimesTask');
        task.positionId = position.id;
        task.startDateTime = newStartTime;
        task.endDateTime = newEndTime;
        if (task.canExecute) {
          return task.execute()
            .then(apiResponse => {
              // TODO: Do something intelligent with this raw API response with position?
              // Instead, we'll just return the existing position
              this.LogService.debug(`Got API response from saving position:`, apiResponse);
              return position;
            })
            .catch(err => {
              this.LogService.error(`Error saving position:`, err);
              const serverErrorMessage = err.data && err.data.error || "Server error";
              const refId = err.data && err.data.ref_id;
              let message = serverErrorMessage;
              if (refId) {
                message = `${message} (ref id: ${refId})`;
              }
              return this.$q.reject(createError({
                error: 'UnableToSavePositionTimes',
                message,
                response: err,
              }));
            });
        } else {
          const allowOfflineEdit = true;
          if (allowOfflineEdit) {
            return this.TaskQueueService.enqueue(task)
              .then(result => {
                this.LogService.log('Enqueued position save task', task, 'result', result);

                /*
                 * Update position start/end time so that it displays correctly in edit modal
                 * NOTE: We need to UN-shift these back to event/server time
                 */
                const eventTimezoneName = position && position.event && position.event.timezoneName;
                const newStartTimeUnshifted = this.DateTimeService.unshiftDateObjectFromLocal(newStartTime, eventTimezoneName);
                const newEndTimeUnshifted = this.DateTimeService.unshiftDateObjectFromLocal(newEndTime, eventTimezoneName);

                // Update position start/end time
                position.start_time = newStartTimeUnshifted;
                position.event.start_timestamp = newStartTimeUnshifted;
                position.end_time = newEndTimeUnshifted;
                position.event.end_timestamp = newEndTimeUnshifted;

                // TODO: Emit an event indicating that position has been updated, so cards can update?
                return position;
              });
          } else {
            return this.$q.reject(createError({
              error: 'CannotSavePositionWhenOffline',
              message: 'Unable to update event when offline',
            }));
          }
        }
      });
  }

  /**
   * Handle FullCalendar event drop
   * See: https://fullcalendar.io/docs/eventDrop
   * @param info
   */
  onCalendarEventDrop(info) {
    this.LogService.debug(`FullCalendarController.onCalendarEventDrop() info:`, info);

    // Attempt to change the start time for this event
    const event = info.event;
    const positionId = event.extendedProps.positionId; // TODO: Pull from event
    const startTime = event.start;
    const endTime = event.end;

    const revertDrop = () => {
      this.LogService.warn(`Reverting calendar drop event`);
      info.revert();
    };

    return this.$q.when(positionId)
      .then(positionId => {
        if (positionId) {
          return this.PositionService.getByIdForStaffingUser(this.staffUser, positionId);
        } else {
          return this.$q.reject(createError({
            error: 'NoPositionIdForCalendarEvent',
            message: 'Calendar event is missing position ID',
          }));
        }
      })
      .then(position => {
        if (position) {
          return this._updatePositionTimes(position, startTime, endTime);
        } else {
          return this.$q.reject(createError({
            error: 'UnableToRetrievePosition',
            message: 'Unable to retrieve position for calendar event drop',
          }));
        }
      })
      .then(updatedPosition => {
        this.LogService.debug(`Got updated position:`, updatedPosition);
        // TODO: Verify that position's start time matches the requested change
        // If it doesn't, then we should revert?

        // TODO: Determine if we should really be doing a full sync after editing position date?
        // Probably yes, because it will change where position gets displayed??
        this.$scope.$emit('sync');
      })
      .catch(err => {
        this.LogService.error(`Error dropping calendar event:`, err);
        revertDrop();
        this.LogService.info(`Displaying error in modal...`);
        return this.showErrorModal(err);
      });
  }

  _setCssVar(key, value) {
    this.$element.get(0).style.setProperty(key, value);
  }

  _fadeOutMorePopover() {
    this._setCssVar('--fc-more-popover-opacity', 0.25);
  }

  _resetStyles() {
    this._setCssVar('--fc-more-popover-opacity', 1.0);
  }

  onCalendarEventDragStart(info) {
    this.LogService.debug(`onCalendarEventDragStart:`, info);

    const popoverEl = info.el.closest('.fc-more-popover');
    if (popoverEl) {
      this.$timeout(() => {
        this.LogService.info(`FullCalendarController.onCalendarEventDragStart() fading out more popover`);
        this._fadeOutMorePopover();

        if (this._calendar) {
          if (this._calendar.disableDropOnMorePopover) {
            this.LogService.info(`FullCalendarController.onCalendarEventDragStart() disabling drop on more popover`);
            this._calendar.disableDropOnMorePopover();
          } else {
            this.LogService.warn(`FullCalendarController.onCalendarEventDragStart() didn't find calendar.disableDropOnMorePopover method`);
          }
        } else {
          this.LogService.warn(`FullCalendarController.onCalendarEventDragStart() don't have calendar reference`);
        }
      });
    } else {
      this.LogService.debug(`I'm not inside popover; ignoring`);
    }
  }

  onCalendarEventDragStop(info) {
    this.LogService.debug(`onCalendarEventDragStop:`, info);
    this.$timeout(() => {
      this._resetStyles();
    });
  }

  /**
   * Handle clicks on calendar event; show tooltip
   * @param info
   */
  onCalendarEventClick(info) {
    const event = info.event;
    const positionId = event.extendedProps.positionId;

    return this.PositionService.getLocalById(positionId)
      .then(position => {
        if (position.isLockedPostRecap) {
         return false 
        }
        this.$scope.$emit('showEditPosition', position);
      })
      .catch(err => {
        console.error("Calendar.editEventClicked() Got error doing position lookup:", err);
        return this.$q.reject(createError({
          error: 'ErrorFindingPositionByIdForEdit',
          message: `Couldn't find position with id ${positionId}`,
        }));
      });
  }

  onDateClick(info) {
    const dateString = this.moment(info.dateStr).format('YYYY-MM-DD');
    const dateStringForAvailability = this.moment(info.dateStr).format('MMM D, YYYY');
    return this.showAvailabilityModal({date: dateStringForAvailability})
      .then(availabilityResult => this.StaffUserService.setAvailability(dateString, availabilityResult))
      .then(response => {
        this.LogService.debug(`Response from setting availability:`, response);
        this.$scope.$emit('sync');
      });
  }

  fullCalendarInitialized(calendar) {
    this.LogService.info(`FullCalendarController.fullCalendarInitialized() calendar:`, calendar);
    this._calendar = calendar;
  }

  _ensureCalendarSize(opts) {
    const calendar = this._calendar;
    const message = opts && opts.message || '';

    if (!calendar) {
      this.LogService.warn(`FullCalendarController._ensureCalendarSize(${message}) no calendar; can't execute`);
      return false;
    }

    const componentEl = this.$element.find('.fullcalendar-component').get(0);
    if (!componentEl) {
      this.LogService.warn(`FullCalendarController._ensureCalendarSize(${message}) no calendar component; can't execute`);
      return false;
    }

    const aspectRatio = this._calendarAspectRatio;
    const minHeightPx = this._calendarMinHeightPx;
    const minAspectRatioWidthPx = minHeightPx * aspectRatio;

    const actualHeight = componentEl && componentEl.offsetHeight;
    const actualWidth = componentEl && componentEl.offsetWidth;
    const actualAspectRatio = actualWidth / actualHeight;

    this.LogService.debug(`FullCalendarController._ensureCalendarSize(${message}) minHeightPx=${minHeightPx} aspectRatio=${aspectRatio} actualHeight=${actualHeight} actualWidth=${actualWidth} actualAspectRatio=${actualAspectRatio}`);

    if (actualWidth) {
      if (actualWidth >= minAspectRatioWidthPx) {
        // Ensure we're using aspectRatio instead of height/contentHeight
        this.LogService.debug(`FullCalendarController._ensureCalendarSize(${message}) setting height=null aspectRatio=${aspectRatio}`);
        calendar.setOption('height', null);
        calendar.setOption('aspectRatio', aspectRatio);
      } else {
        // Use explicit height instead of aspect ratio
        this.LogService.debug(`FullCalendarController._ensureCalendarSize(${message}) setting height=${minHeightPx}`);
        calendar.setOption('height', minHeightPx);
      }
    } else {
      this.LogService.debug(`FullCalendarController._ensureCalendarSize(${message}) Didn't get actualWidth so not doing anything`);
    }
  }

  onWindowResize(info) {
    // See: https://github.com/fullcalendar/fullcalendar/issues/5863#issuecomment-703785292
    this.LogService.debug(`FullCalendarController.onWindowResize() info:`, info);
    this._ensureCalendarSize({ message: 'onWindowResize' });
  }

  onWindowOrientationChange() {
    this.LogService.debug(`FullCalendarController.onWindowOrientationChange() arguments:`, arguments);
    this._ensureCalendarSize({ message: 'onWindowOrientationChange' });

    // brute force - let's ensure calendar size several more times over the next .. few seconds?
    const retriesMs = [1000];
    retriesMs.forEach(ms => {
      this.$timeout(() => {
        this._ensureCalendarSize({ message: `onWindowOrientationChange +${ms}ms` });
      }, ms);
    });
  }

  _positionsChanged() {
    this.LogService.debug(`FulLCalendarController._positionsChanged(); updating events (currently have ${this._events && this._events.length} events)`);
    this._events = this.convertPositionsToEvents(this.staffUser.positions);
    this._eventSources = this.generateEventSources();
    this.LogService.debug(`FullCalendarController._positionsChanged(); now have ${this._events.length} events`);
  }

}

export default FullCalendarController;
